import { useEffect, useRef, useCallback } from "react";
import type { MapRef } from "react-map-gl";
import type { Map as MapboxMap, MapMouseEvent, GeoJSONSource } from "mapbox-gl";
import { reverseGeocode } from "@/services/geocode";
import type { DrawnPolygon } from "@/stores/mapStore";
import { useToastStore } from "@/stores/toastStore";

const DRAW_SOURCE = "draw-source";
const DRAW_FILL = "draw-fill";
const DRAW_LINE = "draw-line";
const DRAW_VERTICES = "draw-vertices";
const CLOSE_THRESHOLD_PX = 14;

function emptyFC(): GeoJSON.FeatureCollection {
  return { type: "FeatureCollection", features: [] };
}

function buildDrawData(vertices: [number, number][]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];

  if (vertices.length >= 3) {
    const ring = [...vertices, vertices[0]!];
    features.push({
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [ring] },
      properties: { kind: "polygon" },
    });
  } else if (vertices.length >= 2) {
    features.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates: vertices },
      properties: { kind: "line" },
    });
  }

  for (const coord of vertices) {
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: coord },
      properties: { kind: "vertex" },
    });
  }

  return { type: "FeatureCollection", features };
}

/** Check if two line segments (p1→p2) and (p3→p4) intersect. */
function segmentsIntersect(
  p1: [number, number], p2: [number, number],
  p3: [number, number], p4: [number, number],
): boolean {
  const d1x = p2[0] - p1[0], d1y = p2[1] - p1[1];
  const d2x = p4[0] - p3[0], d2y = p4[1] - p3[1];
  const cross = d1x * d2y - d1y * d2x;
  if (Math.abs(cross) < 1e-10) return false;
  const t = ((p3[0] - p1[0]) * d2y - (p3[1] - p1[1]) * d2x) / cross;
  const u = ((p3[0] - p1[0]) * d1y - (p3[1] - p1[1]) * d1x) / cross;
  return t > 0 && t < 1 && u > 0 && u < 1;
}

/** Check if a polygon (open ring) has any self-intersecting edges. */
function isSelfIntersecting(coords: [number, number][]): boolean {
  const n = coords.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    const a1 = coords[i]!, a2 = coords[(i + 1) % n]!;
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue;
      const b1 = coords[j]!, b2 = coords[(j + 1) % n]!;
      if (segmentsIntersect(a1, a2, b1, b2)) return true;
    }
  }
  return false;
}

function addDrawLayers(map: MapboxMap, color: string) {
  if (!map.getSource(DRAW_SOURCE)) {
    map.addSource(DRAW_SOURCE, { type: "geojson", data: emptyFC() });
  }
  if (!map.getLayer(DRAW_FILL)) {
    map.addLayer({
      id: DRAW_FILL, type: "fill", source: DRAW_SOURCE,
      filter: ["==", ["get", "kind"], "polygon"],
      paint: { "fill-color": color, "fill-opacity": 0.12, "fill-emissive-strength": 1 },
    });
  }
  if (!map.getLayer(DRAW_LINE)) {
    map.addLayer({
      id: DRAW_LINE, type: "line", source: DRAW_SOURCE,
      filter: ["any", ["==", ["get", "kind"], "polygon"], ["==", ["get", "kind"], "line"]],
      paint: { "line-color": color, "line-width": 2.5, "line-dasharray": [4, 3], "line-opacity": 0.85, "line-emissive-strength": 1 },
    });
  }
  if (!map.getLayer(DRAW_VERTICES)) {
    map.addLayer({
      id: DRAW_VERTICES, type: "circle", source: DRAW_SOURCE, slot: "top",
      filter: ["==", ["get", "kind"], "vertex"],
      paint: { "circle-radius": 4, "circle-color": color, "circle-stroke-width": 1.5, "circle-stroke-color": "#FFFFFF", "circle-emissive-strength": 1 },
    });
  }
}

function removeDrawLayers(map: MapboxMap) {
  try {
    if (map.getLayer(DRAW_VERTICES)) map.removeLayer(DRAW_VERTICES);
    if (map.getLayer(DRAW_LINE)) map.removeLayer(DRAW_LINE);
    if (map.getLayer(DRAW_FILL)) map.removeLayer(DRAW_FILL);
    if (map.getSource(DRAW_SOURCE)) map.removeSource(DRAW_SOURCE);
  } catch { /* layers may already be removed */ }
}

/**
 * Custom map drawing hook. Click-to-place polygon vertices.
 * Double-click or click near the first vertex to close.
 * Escape cancels.
 */
export function useMapDraw(
  mapRef: React.RefObject<MapRef | null>,
  isDrawing: boolean,
  onComplete: (polygon: DrawnPolygon) => void,
  onCancel: () => void,
  language: string,
  entityColor: string,
) {
  const verticesRef = useRef<[number, number][]>([]);
  const onCompleteRef = useRef(onComplete);
  const onCancelRef = useRef(onCancel);
  const entityColorRef = useRef(entityColor);
  onCompleteRef.current = onComplete;
  onCancelRef.current = onCancel;
  entityColorRef.current = entityColor;

  const getMap = useCallback((): MapboxMap | null => {
    const ref = mapRef.current;
    if (!ref) return null;
    return (ref as unknown as { getMap?: () => MapboxMap }).getMap?.() ?? null;
  }, [mapRef]);

  useEffect(() => {
    const map = getMap();
    if (!map) return;

    if (!isDrawing) {
      removeDrawLayers(map);
      map.doubleClickZoom.enable();
      verticesRef.current = [];
      return;
    }

    // Enter draw mode
    verticesRef.current = [];
    map.doubleClickZoom.disable();
    addDrawLayers(map, entityColorRef.current);

    function updateSource() {
      const source = map!.getSource(DRAW_SOURCE) as GeoJSONSource | undefined;
      source?.setData(buildDrawData(verticesRef.current));
    }

    function finish() {
      const coords = [...verticesRef.current];
      if (coords.length < 3) return;

      if (isSelfIntersecting(coords)) {
        useToastStore.getState().addToast({
          type: "error",
          message: "Polygon edges cannot cross. Try drawing again.",
        });
        verticesRef.current = [];
        updateSource();
        return;
      }

      const lngs = coords.map((c) => c[0]);
      const lats = coords.map((c) => c[1]);
      const centerLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
      const centerLat = lats.reduce((a, b) => a + b, 0) / lats.length;
      const lang = language === "no" ? "no" : language === "sv" ? "sv" : "en";
      reverseGeocode(centerLng, centerLat, lang).then((name) => {
        onCompleteRef.current({ coordinates: coords, name: name || "Custom area" });
      });
    }

    function handleClick(e: MapMouseEvent) {
      const vertices = verticesRef.current;
      const coord: [number, number] = [e.lngLat.lng, e.lngLat.lat];

      // Close if clicking near first vertex
      if (vertices.length >= 3) {
        const first = vertices[0]!;
        const firstScreen = map!.project(first);
        const clickScreen = map!.project(coord);
        const dx = firstScreen.x - clickScreen.x;
        const dy = firstScreen.y - clickScreen.y;
        if (Math.sqrt(dx * dx + dy * dy) < CLOSE_THRESHOLD_PX) {
          finish();
          return;
        }
      }

      vertices.push(coord);
      updateSource();
    }

    function handleDblClick(e: MapMouseEvent) {
      e.preventDefault();
      // The dblclick fires after two click events, so remove the extra vertex
      // added by the second click of the double-click
      if (verticesRef.current.length > 3) {
        verticesRef.current.pop();
      }
      if (verticesRef.current.length >= 3) {
        finish();
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onCancelRef.current();
      }
    }

    map.on("click", handleClick);
    map.on("dblclick", handleDblClick);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      map.off("click", handleClick);
      map.off("dblclick", handleDblClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDrawing, getMap, language]); // entityColor excluded — color updates handled by separate effect below

  // ── Update draw layer colors without tearing down the effect ──
  useEffect(() => {
    if (!isDrawing) return;
    const map = getMap();
    if (!map) return;
    if (map.getLayer(DRAW_FILL)) (map as any).setPaintProperty(DRAW_FILL, "fill-color", entityColor);
    if (map.getLayer(DRAW_LINE)) (map as any).setPaintProperty(DRAW_LINE, "line-color", entityColor);
    if (map.getLayer(DRAW_VERTICES)) (map as any).setPaintProperty(DRAW_VERTICES, "circle-color", entityColor);
  }, [entityColor, isDrawing, getMap]);
}
